import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { allowedSSOHostings } from "../../shared/ssoHostings";

import { userActions,sso_redirect_handler  } from './_redux/authActions';

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

const initialValues = {
  username: "",
  password: "",
};

function Login(props) {
  const { requestActive, sso_redirect_handler, login, login_sso,t } = props;
  const [loading, setLoading] = useState(requestActive);
  const [status, setStatus] = useState("");
  const hasHandledSSO = React.useRef(false);
  const currentHost = window.location.hostname;
  const isSSOEnabled = allowedSSOHostings.includes(currentHost);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required(t("Required field")),
    password: Yup.string().required(t("Required field")),
  });

  const LoginSchemaSso = Yup.object().shape({
    username: Yup.string().required(t("Required field")),
  });

  // Handle SSO only once, even if useEffect is re-triggered
  React.useEffect(() => {
    setLoading(requestActive);

    const handleSsoLogin = async () => {
      if (hasHandledSSO.current) return;
      hasHandledSSO.current = true;

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (code) {
        console.log("SSO Authorization Code Detected, Attempting Login...");
        try {
          const response = await sso_redirect_handler();
          console.log("this is the response")
          console.log(response)
          if (response?.redirect) {
            console.log("Redirecting to", response.redirect);
            window.location.href = response.redirect;
            return;
          }

          if (response?.success) {
            console.log("SSO Login Successful:", response);
          } else {
            setStatus(t("The login details are incorrect or SSO failed."));
          }
        } catch (error) {
          console.error("Error during SSO login handle sso:", error);
          setStatus(t("The SSO login process failed. Please try again."));
        }
      }
    };

    handleSsoLogin();
  }, [requestActive, sso_redirect_handler,t]);

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        await login(values.username, values.password);
      } catch {
        setStatus(t("The login details are incorrect"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const formik_sso = useFormik({
    initialValues,
    validationSchema: LoginSchemaSso,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        const response = await login_sso(values.username, false);

        if (response?.redirect) {
          console.log("Redirecting to", response.redirect);
          window.location.href = response.redirect;
        } else if (!response?.success) {
          setStatus(t("Authorization code not found in the URL or initial call for SSO failed"));
        }
      } catch (error) {
        console.error("SSO login process error:", error);
        setStatus(t("The SSO login process failed. Please try again."));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik_sso.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const buttonId = e.nativeEvent.submitter.id;

    if (buttonId === "kt_login_signin_submit") {
      formik.handleSubmit();
    } else if (buttonId === "kt_login_signin_submit_sso") {
      formik_sso.handleSubmit();
    }
  };

  return (
    <div className="login-form login-signin" id="kt_login_signin_form">
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">{t("Login Account")}</h3>
        <p className="text-muted font-weight-bold">{t("Enter your username and password")}</p>
      </div>

      <form onSubmit={handleSubmit} className="form fv-plugins-bootstrap fv-plugins-framework">
        {formik.status && (
          <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">{formik.status}</div>
          </div>
        )}

        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder={t("Username")}
            type="text"
            name="username"
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("username")}`}
            value={formik.values.username || formik_sso.values.username}
            onChange={(e) => {
              formik.handleChange(e);
              formik_sso.handleChange(e);
            }}
            onBlur={(e) => {
              formik.handleBlur(e);
              formik_sso.handleBlur(e);
            }}
          />
          {formik.touched.username && formik.errors.username && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.username}</div>
            </div>
          )}
        </div>

        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder={t("Password")}
            type="password"
            name="password"
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("password")}`}
            {...formik.getFieldProps("password")}
          />
          {formik.touched.password && formik.errors.password && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.password}</div>
            </div>
          )}
        </div>

        <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
          <button
            id="kt_login_signin_submit"
            type="submit"
            disabled={loading}
            className="btn btn-primary font-weight-bold px-9 py-4 my-3"
          >
            <span>{t("Sign In")}</span>
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </button>
          {isSSOEnabled && (
          <button
            id="kt_login_signin_submit_sso"
            type="submit"
            disabled={loading}
            className="btn btn-primary font-weight-bold px-9 py-4 my-3"
          >
            <span>{t("Sign In With SSO")}</span>
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </button>
          )}
        </div>
      </form>
    </div>
  );
}

function mapState(state) {
  const { loggedIn, requestActive } = state.auth;
  return { loggedIn, requestActive };
}

const actionCreators = {
  login: userActions.login,
  logout: userActions.logout,
  login_sso: userActions.login_sso,
  sso_redirect_handler,
};

export default withTranslation()(connect(mapState, actionCreators)(Login));
